import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import type { AxiosError } from 'axios'
import axios from 'axios'
import { isObjectLike } from 'lodash-es'
import qs from 'qs'

import { TOAST_STR } from '@/constant'
import { CommonError } from '@/dto'
import type { ResErr } from '@/types/common/api'

import { deleteNullField } from '../common'
import { envUtils } from '../envUtils'
import { datadogClient } from '../modules/datadog/datadogClient'
import { toastError } from '../toast'
import { wrapErrorHandlerWithJwt, wrapRequestInterceptorWithJwt } from './jwtInterceptors'

const BASE_URL = (() => {
  if (envUtils.isLive) {
    return `https://homepage-api.${domainProvider.TARGET_DOMAIN}`
  }
  if (envUtils.isStaging) {
    return `https://homepage-api-staging.${domainProvider.TARGET_DOMAIN}`
  }

  return `https://homepage-api-dev.${domainProvider.TARGET_DOMAIN}`
})()

const homepageAxios = axios.create({
  baseURL: BASE_URL,
  headers: {
    common: {
      'x-internal': 'mathFLAT',
    },
  },
  paramsSerializer: (params) => {
    // params가 null인 값은 서버로 보내지 않음
    const newParams = isObjectLike(params) ? deleteNullField(params) : params
    return qs.stringify(newParams, { arrayFormat: 'comma' })
  },
})

homepageAxios.interceptors.response.use(
  (response) => response,
  wrapErrorHandlerWithJwt(
    async (error: unknown) => {
      const axiosError = error as AxiosError<ResErr>
      datadogClient.sendError(axiosError)

      if (axiosError.response) {
        const {
          config,
          data: { message, code, data },
          status,
        } = axiosError.response
        const err = new CommonError(message, { type: 'API', status, code, data, config })

        if (status === 500) {
          toastError(TOAST_STR.errorMessage)
        }

        return Promise.reject<CommonError>(err)
      }

      return Promise.reject(axiosError)
    },
    async (originalRequest, newAccessToken) => {
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
      return homepageAxios(originalRequest)
    },
  ),
)

export default homepageAxios
